import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { Observable, catchError, combineLatest, map, of, switchMap } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { LocationRouteCffService } from '@fcom/core';
import { TripType } from '@fcom/core/interfaces';
import { finShare } from '@fcom/rx';
import { LanguageService } from '@fcom/ui-translate';
import { DestinationRouteInfo, PriceCalendarService } from '@fcom/common';
import { resolveTripTypeByHaul } from '@fcom/common/utils';
import { OndMarketingOffersService, transformLocationCode } from '@fcom/ond-marketing-landing-core/services';
import { OndLocationsResponse } from '@fcom/ond-marketing-landing-core/interfaces';

@Component({
  selector: 'cms-destination-flight-info',
  templateUrl: 'cms-destination-flight-info.component.html',
  styleUrls: ['cms-destination-flight-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CmsDestinationFlightInfoComponent {
  private route = inject(ActivatedRoute);
  private priceCalendarService = inject(PriceCalendarService);
  private locationRouteCffService = inject(LocationRouteCffService);
  private ondMarketingOffersService = inject(OndMarketingOffersService);
  private languageService = inject(LanguageService);

  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TripType = TripType;

  private readonly route$: Observable<{ origin: string; destination: string }> = this.route.params.pipe(
    map((params) => ({
      origin: transformLocationCode(params['originLocationCode']),
      destination: transformLocationCode(params['destinationLocationCode']),
    })),
    finShare()
  );

  private readonly locationData$: Observable<OndLocationsResponse> = this.route$.pipe(
    switchMap(({ destination }) =>
      destination
        ? this.ondMarketingOffersService
            .getLocationData([destination])
            .pipe(catchError(() => of({} as OndLocationsResponse)))
        : of({} as OndLocationsResponse)
    ),
    finShare()
  );

  private readonly tripType$: Observable<TripType> = this.route$.pipe(
    switchMap(({ origin, destination }) =>
      resolveTripTypeByHaul(this.locationRouteCffService, origin, destination, this.languageService.localeValue)
    ),
    finShare()
  );

  private readonly routeInfo$: Observable<DestinationRouteInfo> = combineLatest([this.route$, this.tripType$]).pipe(
    switchMap(([{ origin, destination }, tripType]) =>
      this.priceCalendarService.getDestinationRouteInfo({ origin, destination, tripType })
    ),
    finShare()
  );

  private readonly destination = toSignal(this.route$.pipe(map(({ destination }) => destination)), {
    initialValue: '',
  });
  private readonly locationData = toSignal(this.locationData$);
  private readonly tripType = toSignal(this.tripType$, { initialValue: TripType.ONEWAY });
  private readonly routeInfo = toSignal(this.routeInfo$);

  readonly cityName = computed<string | undefined>(() => this.locationData()?.[this.destination()]?.cityName);
  readonly picture = computed(() => this.locationData()?.[this.destination()]?.picture ?? undefined);

  readonly startingFromPrice = computed(() => this.routeInfo()?.startingFromPrice);
  readonly duration = computed(() => this.routeInfo()?.duration);
  readonly airportCodes = computed<string[]>(() => this.routeInfo()?.airportCodes ?? []);
  readonly isDirect = computed<boolean>(() => this.routeInfo()?.isDirect ?? false);
  readonly isRoundTrip = computed<boolean>(() => this.tripType() === TripType.RETURN);
}
